"use client";

import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { generateEmbedUrl, parseVideoUrl } from "@/utilities/video/parseVideoUrl";

export interface VideoModalProps {
    isOpen: boolean;
    onClose: () => void;
    video: {
        url?: string | null;
        platform?: string | null;
        embedCode?: string | null;
    };
    videoKind?: "short" | "standard" | null;
}

export const VideoModal: React.FC<VideoModalProps> = ({ isOpen, onClose, video, videoKind }) => {
    const embedContainerRef = useRef<HTMLDivElement>(null);

    // Lock body scroll when modal is open, compensating for scrollbar width to prevent layout shift
    useEffect(() => {
        if (isOpen) {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = "hidden";
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        } else {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
        }
        return () => {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
        };
    }, [isOpen]);

    // Close on ESC key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isOpen) {
                onClose();
            }
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [isOpen, onClose]);

    // Script injection for raw embed code (Instagram, TikTok, etc.)
    useEffect(() => {
        const container = embedContainerRef.current;
        if (!container || !video.embedCode) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(video.embedCode, "text/html");
        const scripts = doc.querySelectorAll("script");
        const addedScripts: HTMLScriptElement[] = [];

        scripts.forEach((originalScript) => {
            const src = originalScript.getAttribute("src");
            if (src && document.querySelector(`script[src="${CSS.escape(src)}"]`)) {
                return;
            }
            const script = document.createElement("script");
            Array.from(originalScript.attributes).forEach((attr) => {
                script.setAttribute(attr.name, attr.value);
            });
            if (originalScript.textContent) {
                script.textContent = originalScript.textContent;
            }
            script.onload = () => {
                (window as any).instgrm?.Embeds?.process();
            };
            container.appendChild(script);
            addedScripts.push(script);
        });

        // If Instagram embed.js is already loaded, reprocess to handle new blockquotes
        (window as any).instgrm?.Embeds?.process();

        return () => {
            addedScripts.forEach((script) => script.remove());
        };
    }, [video.embedCode, isOpen]);

    if (!isOpen) return null;

    const isShort = videoKind === "short";
    const aspectClass = isShort ? "aspect-[9/16]" : "aspect-video";

    // Try iframe-based embed first
    let iframeSrc: string | null = null;
    if (video.url) {
        const parsed = parseVideoUrl(video.url);
        if (parsed) {
            const embedUrl = generateEmbedUrl(parsed.platform, parsed.videoId);
            if (embedUrl) {
                iframeSrc = `${embedUrl}${embedUrl.includes("?") ? "&" : "?"}autoplay=1`;
            }
        }
    }

    // Determine sizing based on video kind
    const sizeClass = isShort
        ? ""
        : "max-w-[90vw] max-h-[90vh] w-full md:w-[80vw] lg:w-[70vw]";

    const sizeStyle: React.CSSProperties | undefined = isShort
        ? { height: "90vh", width: "min(calc(90vh * 9 / 16), 90vw)" }
        : undefined;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/80 cursor-pointer" onClick={onClose} aria-hidden="true" />

            {/* Content */}
            <div className={`relative ${sizeClass}`} style={sizeStyle} role="dialog" aria-modal="true">
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute -top-3 -right-3 z-10 p-1.5 rounded-full bg-white/20 text-white hover:bg-white/30 shadow-lg transition-colors cursor-pointer"
                    aria-label="Close"
                >
                    <X className="w-5 h-5" />
                </button>

                {iframeSrc ? (
                    <iframe
                        src={iframeSrc}
                        title="Video player"
                        className={`w-full ${aspectClass} rounded-lg`}
                        allow="autoplay; fullscreen; encrypted-media"
                        allowFullScreen
                    />
                ) : video.embedCode ? (
                    <div
                        ref={embedContainerRef}
                        className="flex items-center justify-center [&>iframe]:max-w-full"
                        dangerouslySetInnerHTML={{
                            __html: video.embedCode
                                .replace(/<script[\s\S]*?<\/script>/gi, "")
                                .replace(/<script\b[^>]*\/>/gi, ""),
                        }}
                    />
                ) : null}
            </div>
        </div>
    );
};
